import * as THREE from 'three';
import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGameStore } from '../stores/gameStore.js';

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform float uTime;
  uniform float uSpeed;
  uniform float uOpacity;
  uniform float uDensity;
  uniform vec3 uColor;
  uniform float uRadiusScale;
  
  varying vec2 vUv;
  
  // Basic 2D random per generar el soroll
  float random(vec2 st) {
      return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
  }

  void main() {
    // L'UV es desplaça longitudinalment. Sumem el temps perquè flueixi de la punta fins a la base de la càmera (-x)
    vec2 pos = vec2(vUv.x * 120.0 * uRadiusScale, vUv.y * 3.0 + (uTime * uSpeed));
    
    // Generem els carrils longitudinals
    float noise = random(vec2(floor(pos.x), floor(pos.y)));
    
    // Pinzellem els extrems de les cassetlles perquè no siguin rectangles
    float segmentY = fract(pos.y); 
    // Més fi i esmolat als extrems (0.0 a 0.2 i de 1.0 a 0.6 tallant suaument)
    float lineShape = smoothstep(0.0, 0.2, segmentY) * smoothstep(1.0, 0.5, segmentY);
    
    // El "uDensity" serà controlat per JS. Com més a prop de 1.0, menys línies. Com més lluny, més línies apareixen de cop.
    float alpha = smoothstep(uDensity, uDensity + 0.04, noise) * lineShape;
    
    // Esvaïment brutal al punt de fuga (0.95-1.0) per evitar grumolls / "formes rares" que es trepigen geomètricament
    // També l'esvaim a 0.0 perquè no matin a la càmara.
    float fade = smoothstep(0.1, 0.2, vUv.y) * smoothstep(0.95, 0.7, vUv.y);
    
    gl_FragColor = vec4(uColor, alpha * fade * uOpacity);
  }
`;

function TunnelLayer({ radiusMultiplier, speedMultiplier, baseOpacity }) {
  const meshRef = useRef();
  const phase = useGameStore(state => state.phase);

  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uSpeed: { value: 0.5 * speedMultiplier },
    uOpacity: { value: 0.0 },
    uDensity: { value: 0.995 }, // Quasi cap línia al principi
    uColor: { value: new THREE.Color("#ffffff") }, // 1. COLOR: Només Blanc!
    uRadiusScale: { value: radiusMultiplier }
  }), [radiusMultiplier, speedMultiplier]);

  useFrame((state, delta) => {
    if (meshRef.current) {
      // Pas del temps
      meshRef.current.material.uniforms.uTime.value += delta;

      const camX = state.camera.position.x;
      meshRef.current.position.x = camX + 150;

      // 4. TRANSICIÓ MATEMÀTICA PERFECTA (Sense talls ni salts condicionals):
      let targetOpacity = 0.0;
      let targetSpeed = 0.0;
      let targetDensity = 0.995;

      if (phase === 'playing' || phase === 'launching') {
        targetOpacity = baseOpacity * 0.05; // Molt tènue d'inici
        targetSpeed = 0.5 * speedMultiplier;

        if (camX >= 380) {
          // Progrés lineal precís del 0.0 (a la linia 380) al 1.0 (a la meta 563)
          const progress = THREE.MathUtils.clamp((camX - 380) / (563 - 380), 0, 1);

          // Ajustem exponents per endarrerir una mica l'inici brusc, punt mig perfecte!
          const smoothExp = Math.pow(progress, 4);
          const hyperExp = Math.pow(progress, 12);

          targetSpeed = (0.5 + (smoothExp * 30.0) + (hyperExp * 170.0)) * speedMultiplier;
          targetOpacity = (baseOpacity * 0.05) + (smoothExp * baseOpacity * 0.5) + (hyperExp * baseOpacity * 2.0);

          // Densitat de poques (0.99) a moltes (0.85 baixant el topall superior d'esvaïment per atreure més línies del noise)
          targetDensity = 0.995 - (smoothExp * 0.08) - (hyperExp * 0.065);
        }
      }

      uniforms.uOpacity.value = THREE.MathUtils.lerp(uniforms.uOpacity.value, targetOpacity, 0.05);
      uniforms.uSpeed.value = THREE.MathUtils.lerp(uniforms.uSpeed.value, targetSpeed, 0.05);
      uniforms.uDensity.value = THREE.MathUtils.lerp(uniforms.uDensity.value, targetDensity, 0.05);
    }
  });

  return (
    <mesh ref={meshRef} rotation={[0, 0, -Math.PI / 2]}>
      <coneGeometry args={[22 * radiusMultiplier, 500, 32, 1, true]} />
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent={true}
        depthWrite={false}
        side={THREE.BackSide}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

export default function SpeedLinesTunnel() {
  const phase = useGameStore(state => state.phase);

  return (
    <group visible={phase === 'playing' || phase === 'launching'}>
      {/* Totes les capes BLANQUES. Només varien escalats, radis per donar profunditat i velocitat parallax */}
      <TunnelLayer radiusMultiplier={1.0} speedMultiplier={1.0} baseOpacity={0.8} />
      <TunnelLayer radiusMultiplier={0.85} speedMultiplier={1.3} baseOpacity={0.5} />
      <TunnelLayer radiusMultiplier={1.15} speedMultiplier={0.8} baseOpacity={0.6} />
      <TunnelLayer radiusMultiplier={0.65} speedMultiplier={1.8} baseOpacity={0.3} />
    </group>
  );
}
